import { useLocalStorage } from '../hooks/useLocalStorage';
import React, { createContext, useState, useContext, ReactNode, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

export interface SettingsContextType {
  betaFeatures: Record<string, boolean>;
  handleBetaFeatureToggle: (feature: string) => void;
  enhancedPromptFixing: boolean;
  setEnhancedPromptFixing: (enhancedPromptFixing: boolean) => void;
  activeVoice: string;
  setActiveVoice: (activeVoice: string) => void;
  validateAndSetVoice: (voiceId: string) => void;
}

export type SettingsSection = 'profile' | 'account' | 'preferences' | 'beta' | 'model' | 'voices' | 'help' | 'logout';

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export const useSettings = (): SettingsContextType => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

interface SettingsProviderProps {
  children: ReactNode;
}


export const SettingsProvider: React.FC<SettingsProviderProps> = ({ children }) => {
  const { voices } = useAuth();

  const [enhancedPromptFixing, setEnhancedPromptFixing] = useState<boolean>(() => {
    const storedEnhancedPromptFixing = localStorage.getItem('enhancedPromptFixing');
    return storedEnhancedPromptFixing !== null ? storedEnhancedPromptFixing === 'true' : false;
  });

  const [betaFeatures, setBetaFeatures] = useState<Record<string, boolean>>(() => {
    try {
      const savedFeatures = localStorage.getItem('betaFeatures');
      return savedFeatures ? JSON.parse(savedFeatures) : {};
    } catch {
      return {};
    }
  });

  const [activeVoice, setActiveVoice] = useLocalStorage<string>("activeVoice", "");
  // Helper function to validate and set a voice (expects a voice id)
  const validateAndSetVoice = useCallback((voiceId: string) => {
    if (voices.length === 0) {
      console.warn('No voices available to select from');
      return;
    }

    const voiceExists = voices.some(v => v.id === voiceId);
    if (voiceExists) {
      setActiveVoice(voiceId);
    } else {
      console.warn(`Voice "${voiceId}" not found in available voices, using default`);
      const defaultVoice =
        voices.find(v => v.isDefault) ||
        voices[0];
      setActiveVoice(defaultVoice.id);
    }
  }, [voices, setActiveVoice]);

  // Set default voice when voices are loaded and ensure selected voice exists
  useEffect(() => {
    if (voices.length > 0) {
      // Check if current activeVoice exists in available voices
      const currentVoiceExists = activeVoice && voices.some(v => v.id === activeVoice);

      if (!currentVoiceExists) {
        // Find preferred default voice or fallback to first available
        const defaultVoice =
          voices.find(v => v.isDefault) ||
          voices[0];
        setActiveVoice(defaultVoice.id);
      }
    }
  }, [voices, activeVoice, setActiveVoice]); 

  useEffect(() => {
    localStorage.setItem('enhancedPromptFixing', enhancedPromptFixing.toString());
  }, [enhancedPromptFixing]);

  useEffect(() => {
    localStorage.setItem('betaFeatures', JSON.stringify(betaFeatures));
  }, [betaFeatures]);



  const handleBetaFeatureToggle = useCallback((feature: string) => {
    setBetaFeatures(prev => {
      const newState = {
        ...prev,
        [feature]: !prev[feature]
      };
      return newState;
    });
  }, []);


  const contextValue = React.useMemo(() => ({
    betaFeatures,
    handleBetaFeatureToggle,
    enhancedPromptFixing,
    setEnhancedPromptFixing,
    activeVoice,
    setActiveVoice,
    validateAndSetVoice,
  }), [
    betaFeatures,
    handleBetaFeatureToggle,
    enhancedPromptFixing,
    setEnhancedPromptFixing,
    activeVoice,
    setActiveVoice,
    validateAndSetVoice,
  ]);

  return (
    <SettingsContext.Provider value={contextValue}>
      {children}
    </SettingsContext.Provider>
  );
};
